/**
 * Site-wide content provider.
 * ---------------------------------------------------------------------------
 * The header, footer, mega menu and several pages need the same slow-changing
 * content: site settings, categories, brands, partners and services. Before the
 * backend existed each of those files imported the mock data module directly,
 * which meant CMS-managed content could never reach them.
 *
 * This provider loads all of it once from Firestore and hands it down through
 * hooks. Two deliberate design choices:
 *
 *  1. It is seeded with the bundled default content, so the first paint is
 *     immediate and complete — no spinners in the site chrome, no layout shift,
 *     and a working site even if Firestore is unreachable.
 *  2. It also feeds `lib/labels.js`, so slug -> display-name lookups scattered
 *     through the product components resolve against live CMS data.
 */

import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import {
  categoriesService,
  brandsService,
  partnersService,
  servicesService,
  siteSettingsService,
  seoService,
  customersService,
  toCategory,
  toBrand,
  toPartner,
  toService,
  toCustomer,
  toSiteSettings,
} from "@/services/contentServices";
import { registerLabels } from "@/lib/labels";
import { CATEGORIES as DEFAULT_CATEGORIES } from "@/data/categories";
import { BRANDS as DEFAULT_BRANDS } from "@/data/brands";
import { PARTNERS as DEFAULT_PARTNERS } from "@/data/partners";
import { SERVICES as DEFAULT_SERVICES, WHY_ATHENA as DEFAULT_WHY } from "@/data/services";
import { SITE as DEFAULT_SITE } from "@/data/siteSettings";
import { IMAGES as DEFAULT_IMAGES } from "@/data/images";
import { NAV_LINKS } from "@/data/navigation";

/**
 * Content baked into the page at build time by scripts/prerender.js.
 *
 * Without it the first paint uses the defaults bundled into this JavaScript,
 * which are a snapshot from whenever the app was last built: an old hero, a
 * shorter category list, partners as names rather than logos. Firestore then
 * answers and everything swaps. The client described that as the page
 * loading "raw", and as an image they had already replaced flashing up on
 * every refresh.
 *
 * Rows arrive exactly as Firestore returned them and go through the same
 * mappers a live read uses, so there is no second shape to keep in step.
 */
const bootstrap = typeof window !== "undefined" ? window.__ATHENA__ : null;

const seeded = (rows, map, fallback) =>
  Array.isArray(rows) && rows.length ? rows.map(map) : fallback;

const INITIAL_STATE = {
  site: bootstrap?.site ? toSiteSettings(bootstrap.site) : DEFAULT_SITE,
  categories: seeded(bootstrap?.categories, toCategory, DEFAULT_CATEGORIES),
  brands: seeded(bootstrap?.brands, toBrand, DEFAULT_BRANDS),
  partners: seeded(bootstrap?.partners, toPartner, DEFAULT_PARTNERS),
  services: seeded(bootstrap?.services, toService, DEFAULT_SERVICES),
  whyAthena: bootstrap?.whyAthena?.length ? bootstrap.whyAthena : DEFAULT_WHY,
  seoPages: bootstrap?.seoPages || [],
  customers: seeded(bootstrap?.customers, toCustomer, []),
  loading: true,
  loaded: false,
};

const SiteDataContext = createContext({
  ...INITIAL_STATE,
  images: DEFAULT_IMAGES,
  navigation: { links: NAV_LINKS, productsMega: { categories: [], brands: [] }, servicesMega: [] },
  waLink: () => "",
  refresh: () => {},
});

export function SiteDataProvider({ children }) {
  const [state, setState] = useState(INITIAL_STATE);

  const load = useCallback(async () => {
    // Every one of these degrades to bundled content on failure (see
    // services/_base.js `safe`), so a rejected promise here is not expected —
    // but Promise.all would still be the wrong shape if one ever did.
    const [site, categories, brands, partners, services, whyAthena, seoPages, customers] =
      await Promise.all([
        siteSettingsService.getSiteSettings(),
        categoriesService.getCategories(),
        brandsService.getBrands(),
        partnersService.getPartners(),
        servicesService.getServices(),
        siteSettingsService.getWhyAthena(),
        seoService.getSeoPages(),
        customersService.getCustomers(),
      ]);

    setState({
      site,
      categories,
      brands,
      partners,
      services,
      whyAthena,
      seoPages,
      customers,
      loading: false,
      loaded: true,
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    load().catch((error) => {
      // eslint-disable-next-line no-console
      console.error("[SiteData] failed to load site content", error);
      if (!cancelled) setState((prev) => ({ ...prev, loading: false, loaded: true }));
    });
    return () => {
      cancelled = true;
    };
  }, [load]);

  // Keep the slug -> name lookups used by product components in sync.
  useEffect(() => {
    registerLabels({ categories: state.categories, brands: state.brands });
  }, [state.categories, state.brands]);

  const value = useMemo(() => {
    const { site, categories, brands, services } = state;

    const images = {
      ...DEFAULT_IMAGES,
      ...(site.media || {}),
      category: { ...DEFAULT_IMAGES.category, ...(site.media?.category || {}) },
    };

    const navigation = {
      links: NAV_LINKS,
      productsMega: {
        categories: categories.map((category) => ({
          name: category.name,
          to: `/products?category=${category.slug}`,
          subcategories: (category.subcategories || []).map((sub) => ({
            name: sub.name,
            to: `/products?category=${category.slug}&subcategory=${sub.slug}`,
          })),
        })),
        brands: brands
          .filter((brand) => (brand.categories || []).length > 0)
          .map((brand) => ({ name: brand.name, to: `/products?brand=${brand.slug}` })),
      },
      servicesMega: services.map((service) => ({
        name: service.name,
        to: `/services/${service.slug}`,
      })),
    };

    const waLink = (message) => {
      const base = site.whatsapp?.url || `https://wa.me/${site.whatsapp?.number || ""}`;
      const text = message || site.whatsapp?.defaultMessage || "";
      return `${base}?text=${encodeURIComponent(text)}`;
    };

    return { ...state, images, navigation, waLink, refresh: load };
  }, [state, load]);

  return <SiteDataContext.Provider value={value}>{children}</SiteDataContext.Provider>;
}

export const useSiteData = () => useContext(SiteDataContext);

// Focused hooks so a component only re-reads what it actually needs.
export const useSite = () => useSiteData().site;
export const useCategories = () => useSiteData().categories;
export const useBrands = () => useSiteData().brands;
export const usePartners = () => useSiteData().partners;
export const useServices = () => useSiteData().services;
export const useWhyAthena = () => useSiteData().whyAthena;
export const useCustomers = () => useSiteData().customers;
export const useSiteImages = () => useSiteData().images;
export const useNavigation = () => useSiteData().navigation;
export const useWaLink = () => useSiteData().waLink;

/**
 * CMS-managed SEO overrides for a route.
 *
 * `routeKey` is a stable identifier the CMS lists ("home", "products",
 * "contact"…). Returns null when nothing has been configured, so the page's own
 * defaults stand.
 */
export const useSeoOverride = (routeKey) => {
  const { seoPages } = useSiteData();
  if (!routeKey || !seoPages?.length) return null;
  return seoPages.find((page) => page.id === routeKey || page.route === routeKey) || null;
};

export default SiteDataContext;
